import clsx from 'clsx';
import React, { Fragment, useEffect } from 'react';
import ReactHtmlParser from 'react-html-parser';

import useEventTracking from '@hooks/useEventTracking';
import { Author } from '@interfaces/authors';
import { Pickup } from '@interfaces/homepage';
import { ClickTarget, TrackingLocation, click } from '@tracking';

import FadeWrapper from '../../FadeWrapper';
import Takeaway from './Takeaway';
import styles from './styles.module.scss';

interface BodyProps {
  addendum?: string | null;
  authors: Author[];
  canViewFullArticle: boolean;
  className?: string;
  freeBlurb: string;
  fullText?: string | null;
  isSEOBot: boolean;
  pickups?: Pickup[];
  takeaway?: string | null;
}

const Body = ({
  addendum,
  authors,
  canViewFullArticle,
  className,
  freeBlurb,
  fullText,
  isSEOBot,
  takeaway,
}: BodyProps) => {
  const { trackEvent } = useEventTracking({
    location: TrackingLocation.ContentBody,
  });

  useEffect(() => {
    const handleAnchorClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Check if the clicked element is an anchor tag
      if (target.nodeName === 'A') {
        trackEvent(
          click({
            clickTarget: ClickTarget.AnchorLink,
            clickLink: (target as HTMLAnchorElement).href,
            clickText: target.innerText,
          })
        );
      }
    };

    const articleBody = document.querySelector('.clipperable-content');
    articleBody.addEventListener('click', handleAnchorClick);
    return () => {
      articleBody.removeEventListener('click', handleAnchorClick);
    };
  }, [trackEvent]);

  useEffect(() => {
    const handleMessage = (event) => {
      const datawrapperHeight = event.data['datawrapper-height'];
      if (typeof datawrapperHeight !== 'undefined') {
        const articleBody = document.querySelector('.clipperable-content');
        const iframes = articleBody.querySelectorAll('iframe');
        for (const chartId in datawrapperHeight) {
          for (let i = 0; i < iframes.length; i++) {
            if (iframes[i].contentWindow === event.source) {
              const iframe = iframes[i];
              iframe.style.height = datawrapperHeight[chartId] + 'px';
            }
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  return (
    <div className={clsx(styles.articleBody, 'clipperable-content')}>
      {canViewFullArticle ? (
        ReactHtmlParser(freeBlurb)
      ) : (
        <FadeWrapper>{ReactHtmlParser(freeBlurb)}</FadeWrapper>
      )}
      {takeaway && <Takeaway takeaway={takeaway} />}
      {fullText && (
        <div className={clsx({ 'seo-paywall-content': isSEOBot }, className)}>
          {ReactHtmlParser(fullText)}
        </div>
      )}
      {canViewFullArticle && (
        <div className="border-0 border-t border-solid border-grayscale-beige pt-28 sm:pt-48">
          {addendum && (
            <p className="!font-suisse-intl !text-button-lg !font-light text-black">
              {ReactHtmlParser(addendum)}
            </p>
          )}
          {authors.length > 0 &&
            authors.map((author, index) => (
              <Fragment key={index}>
                {author.bio && (
                  <p className="font-noto-serif text-body-articles !font-light italic last-of-type:mb-0">
                    {ReactHtmlParser(author.bio)}
                  </p>
                )}
              </Fragment>
            ))}
        </div>
      )}
    </div>
  );
};

export default Body;
